import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { UNCATEGORIZED_ID } from '../constants/appConstants';

const endpoint = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_SERVER_URL : process.env.REACT_APP_LOCAL_SERVER_URL;

/**
 * CategoryNode - Renders a single category with its children in a tree structure
 */
const CategoryNode = ({ product_id, category, selectedCategories, setSelectedCategories, getAccessToken, level = 0, onProductUnhidden, setIsLoading }) => {
    const hasChildren = category.children && category.children.length > 0;

    const handleUpdateCategories = async (categories) => {
        setIsLoading(true);
        try {
            const token = await getAccessToken();
            const response = await fetch(`${endpoint}api/update-product-categories`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ product_id, categories: categories.map((c) => c.id) }),
            });

            const data = await response.json();

            if (!response.ok || data.error) {
                alert(`Error: ${data.error || 'An unknown error occurred.'}`);
                return;
            }

            // If the product was unhidden, backend removed uncategorized automatically
            // So we need to filter it out from the categories we're setting
            let updatedCategories = categories;
            if (data.unhidden) {
                updatedCategories = categories.filter(c => c.id !== UNCATEGORIZED_ID);
                onProductUnhidden();
            }

            setSelectedCategories(updatedCategories);
        } catch (error) {
            console.error("Error:", error);
            alert("Failed to update categories. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const onCategoryClicked = (e) => {
        const val = parseInt(e.target.value);
        const name = e.target.name;
        let newCategories;
        if (selectedCategories.map((c) => c.id).includes(val)) {
            newCategories = selectedCategories.filter(item => item.id !== val);
        } else {
            newCategories = [...selectedCategories, { id: val, name: name }];
        }
        handleUpdateCategories(newCategories);
    };

    return hasChildren ? (
        <details open className="category-node" style={{ marginLeft: `${level * 16}px` }}>
            <summary>
                <label>{category.name}
                    <input
                        type="checkbox"
                        name={category.name}
                        value={category.id}
                        checked={selectedCategories.map((c) => c.id).includes(category.id)}
                        onChange={onCategoryClicked}
                    />
                </label>
            </summary>
            {category.children.map((child) => (
                <CategoryNode
                    product_id={product_id}
                    key={child.id}
                    category={child}
                    level={level + 1}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    getAccessToken={getAccessToken}
                    onProductUnhidden={onProductUnhidden}
                    setIsLoading={setIsLoading}
                />
            ))}
        </details>
    ) : (
        <div className="category-leaf" style={{ marginLeft: `${level * 16}px` }}>
            <label>{category.name}
                <input
                    type="checkbox"
                    name={category.name}
                    value={category.id}
                    checked={selectedCategories.map((c) => c.id).includes(category.id)}
                    onChange={onCategoryClicked}
                />
            </label>
        </div>
    );
};

/**
 * CategoryTree - Renders the full category tree
 */
const CategoryTree = ({ product_id, allCategories, selectedCategories, setSelectedCategories, getAccessToken, onProductUnhidden, setIsLoading }) => {
    return (
        <div className="category-tree">
            {allCategories.children.map((category) => (
                <CategoryNode
                    product_id={product_id}
                    key={category.id}
                    category={category}
                    level={1}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    getAccessToken={getAccessToken}
                    onProductUnhidden={onProductUnhidden}
                    setIsLoading={setIsLoading}
                />
            ))}
        </div>
    );
};

/**
 * AdminProductModifiers - Admin controls for modifying product properties
 */
export const AdminProductModifiers = ({ item, availableCategories, isLoading, setIsLoading }) => {
    const { getAccessToken } = useAuth();
    const { product_id, featured, hidden, gender, categories, admin_deleted, out_of_stock } = item;

    const handleToggle = async (newValue, setter, key) => {
        setter(newValue);
        setIsLoading(true);

        try {
            const token = await getAccessToken();
            const response = await fetch(`${endpoint}api/update-product`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ value: newValue, key, product_id }),
            });

            if (!response.ok) {
                throw new Error("Failed to update product");
            }
        } catch (error) {
            console.error("Error:", error);
            alert("Failed to update product. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const [isFeatured, setIsFeatured] = useState(featured);
    const [isHidden, setIsHidden] = useState(hidden);
    const [currGender, setCurrGender] = useState(gender);
    const [adminDeleted, setAdminDeleted] = useState(admin_deleted);
    const [isOutOfStock, setIsOutOfStock] = useState(out_of_stock);
    const [selectedCategories, setSelectedCategories] = useState(
        categories ? JSON.parse(`[${categories}]`) : []
    );

    const MEN = 'Men';
    const WOMEN = 'Women';
    const UNISEX = 'Unisex';

    // Handler for when product is automatically unhidden due to categorization
    const handleProductUnhidden = () => {
        // Uncheck the hidden checkbox
        setIsHidden(false);

        // Remove uncategorized from selected categories
        setSelectedCategories(prevCategories =>
            prevCategories.filter(cat => cat.id !== UNCATEGORIZED_ID)
        );
    };

    return (
        <div style={styles.modifiers}>
            <div style={styles.singleLineModifiers}>
                <label onClick={(e) => e.stopPropagation()}>
                    Featured:
                    <input
                        type="checkbox"
                        checked={isFeatured}
                        onChange={() => handleToggle(!isFeatured, setIsFeatured, 'featured')}
                    />
                </label>
                <label onClick={(e) => e.stopPropagation()}>
                    Hidden:
                    <input
                        type="checkbox"
                        checked={isHidden}
                        onChange={() => handleToggle(!isHidden, setIsHidden, 'hidden')}
                    />
                </label>
                <label onClick={(e) => e.stopPropagation()}>
                    DELETE:
                    <input
                        type="checkbox"
                        checked={adminDeleted}
                        onChange={() => handleToggle(!adminDeleted, setAdminDeleted, 'admin_deleted')}
                    />
                </label>
                <label onClick={(e) => e.stopPropagation()}>
                    Out of Stock:
                    <input
                        type="checkbox"
                        checked={isOutOfStock}
                        onChange={() => handleToggle(!isOutOfStock, setIsOutOfStock, 'out_of_stock')}
                    />
                </label>
            </div>
            <div style={styles.singleLineModifiers}>
                <label onClick={(e) => e.stopPropagation()}>
                    Men:
                    <input
                        type="checkbox"
                        checked={currGender === MEN}
                        onChange={() => {
                            if (currGender === MEN) return;
                            handleToggle(MEN, setCurrGender, 'gender');
                        }}
                    />
                </label>
                <label onClick={(e) => e.stopPropagation()}>
                    Women:
                    <input
                        type="checkbox"
                        checked={currGender === WOMEN}
                        onChange={() => {
                            if (currGender === WOMEN) return;
                            handleToggle(WOMEN, setCurrGender, 'gender');
                        }}
                    />
                </label>
                <label onClick={(e) => e.stopPropagation()}>
                    Unisex:
                    <input
                        type="checkbox"
                        checked={currGender === UNISEX}
                        onChange={() => {
                            if (currGender === UNISEX) return;
                            handleToggle(UNISEX, setCurrGender, 'gender');
                        }}
                    />
                </label>
            </div>
            <label onClick={(e) => e.stopPropagation()}>
                Categories:
                {availableCategories && selectedCategories && availableCategories.length > 0 && (
                    <CategoryTree
                        product_id={product_id}
                        allCategories={availableCategories[0]}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                        getAccessToken={getAccessToken}
                        onProductUnhidden={handleProductUnhidden}
                        setIsLoading={setIsLoading}
                    />
                )}
            </label>
        </div>
    );
};

/**
 * AdminProductTimestamps - Display created and updated timestamps
 */
export const AdminProductTimestamps = ({ created_at, last_updated_at }) => {
    return (
        <div style={styles.timestamps}>
            <div>First Added: {new Date(created_at).toLocaleString()}</div>
            <div>Last Updated: {new Date(last_updated_at).toLocaleString()}</div>
        </div>
    );
};

const styles = {
    modifiers: {
        padding: '10px',
        backgroundColor: '#f9f9f9',
        borderTop: '1px solid #ddd',
        fontSize: '14px',
    },
    singleLineModifiers: {
        display: 'flex',
        gap: '15px',
        marginBottom: '10px',
        flexWrap: 'wrap',
    },
    timestamps: {
        fontSize: '12px',
        color: '#777',
        marginTop: '5px',
        marginBottom: '5px',
    },
};

export default AdminProductModifiers;
